import { useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Icon, ButtonRound } from '@ontruck/orc';

import cx from 'classnames';
import { StopCell } from 'modules/fleet/list';
import { PalletsCell, WeightCell, HeightCell } from 'utils/format';
import { cellWidth } from 'utils/itineraries';

import { ShippersTooltip } from 'modules/fleet/common';

import { getSortableListHover } from 'utils/dnd';

import { ReactComponent as PickupIcon } from 'assets/icons/pickup.svg';
import { ReactComponent as DropoffIcon } from 'assets/icons/dropoff.svg';

const stopTypeIcons = {
  PICKUP: <PickupIcon style={{ width: 16, height: 16 }} />,
  DROPOFF: <DropoffIcon style={{ width: 16, height: 16 }} />,
};

const Types = {
  STOP: 'RoutingStop',
};

const DONE_STATUSES = ['DONE', 'DONE_WITHOUT_MANIFEST'];
const IN_PROGRESS_STATUSES = ['IN_PROGRESS'];

const RoutingStop = ({
  stop,
  id,
  index,
  moveStop,
  onRemove,
  onEditTimes,
  isUpgradingJourney = false,
  onHover,
  onHoverLeft,
  isHovered,
  showDate,
  firstStopTimeFrom,
  shipperOrders,
}) => {
  const handleEditTimes = useCallback(
    (timesEdited) => {
      onEditTimes && onEditTimes(timesEdited, index);
    },
    [index, onEditTimes]
  );

  const handleRemoveStop = useCallback(() => {
    onRemove(index, id);
  }, [index, onRemove, id]);

  const isImprovementStop = isUpgradingJourney && !stop?.upgrade_journey_id;

  const isDone = DONE_STATUSES.includes(stop?.status);
  const isInProgress = IN_PROGRESS_STATUSES.includes(stop?.status);
  const isRemovable = !isDone && !isInProgress;
  const canDrop = !isDone && !isInProgress;
  const isDraggable = canDrop;

  const handleStopHover = useCallback(
    (e) => {
      onHover && onHover(index);
    },
    [index, onHover]
  );

  const handleStopLeave = useCallback(
    (e) => {
      onHoverLeft && onHoverLeft(index);
    },
    [index, onHoverLeft]
  );

  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: Types.STOP,
    hover: getSortableListHover(ref, canDrop, index, moveStop),
  });

  const [{ isDragging }, drag] = useDrag({
    type: Types.STOP,
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => {
      return isDraggable;
    },
  });

  const style = isDragging ? { opacity: 0, cursor: 'grabbing' } : {};

  drag(drop(ref));

  if (!stop) {
    return null;
  }

  const stopOrdinalType = `${index + 1}`;
  const isPendingAppointment = stop?.appointment_required && !stop.has_appointment_time;

  return (
    <div
      ref={ref}
      className={cx('stop', {
        noHover: !isDraggable,
        noDraggable: !isDraggable,
        isHovered: isHovered,
      })}
      style={style}
      data-testid="RoutingStop"
      onMouseEnter={handleStopHover}
      onMouseLeave={handleStopLeave}
    >
      <div
        className={cx('cell cell__nowrap cell__nopadding cell__center cell__dark cell__bold', {
          cell__status__done: isDone,
          cell__status__inProgress: isInProgress,
          cell__status__improvement: isImprovementStop,
        })}
        {...cellWidth('36px')}
        data-testid="RoutingStop_numeral"
      >
        {isDone && <Icon glyph="success" />}
        {isInProgress && <Icon glyph="truck" />}
        {!isDone && !isInProgress && stopOrdinalType}
      </div>
      <div
        className={cx('cell cell__nowrap cell__nopadding cell__center')}
        {...cellWidth('36px')}
        data-testid="RoutingStop_stopType"
      >
        {stopTypeIcons[stop?.stop_type]}
      </div>
      <ShippersTooltip shipperOrders={shipperOrders} placement="left" trigger="mouseenter">
        <StopCell
          firstStopTimeFrom={firstStopTimeFrom}
          label={stop?.address?.label}
          timeFrom={stop?.time_from}
          timeTo={stop?.time_to}
          showTimes={!isPendingAppointment}
          isPendingAppointment={isPendingAppointment}
          allowEditTimes={false}
          showDate={showDate}
          onEditTimes={handleEditTimes}
          {...cellWidth('180px')}
        />
      </ShippersTooltip>
      <div className="cell cell__bold cell__right cell__dark" {...cellWidth('80px')}>
        <PalletsCell
          loadTypes={stop?.load_types}
          realPallets={stop?.real_pallets}
          equivalentPallets={stop?.equivalent_pallets}
          typePallets={stop?.type_pallets}
        />
      </div>
      <div className="cell cell__right cell__dark" {...cellWidth('72px')}>
        <WeightCell weight={stop?.total_weight} />
      </div>
      <div className="cell cell__right cell__dark" {...cellWidth('50px')}>
        <HeightCell height={stop?.item_height} />
      </div>
      <div className="cell cell__grow"></div>
      {isRemovable && !!onRemove && (
        <div className="cell">
          <div className="remove-container" data-testid="RoutingStop_closeBtn">
            <ButtonRound skin="primary" size="XXS" onClick={handleRemoveStop} icon="close" />
          </div>
        </div>
      )}
    </div>
  );
};

const RoutingSummaryStop = ({ stop }) => {
  if (!stop) {
    return null;
  }

  return (
    <div className="stop noHover defaultCursor" data-testid="RoutingStopSummaryStop">
      <div className="cell cell__dark cell__nowrap cell__nopadding" {...cellWidth('36px')}>
        &nbsp;
      </div>
      <div className="cell cell__dark cell__nowrap cell__nopadding" {...cellWidth('36px')}></div>
      <div className="cell cell__dark cell_nowrap" {...cellWidth('160px')}></div>
      <div className="cell cell__dark cell__bold cell__right cell__dark" {...cellWidth('80px')}>
        <PalletsCell
          loadTypes={stop?.load_types}
          realPallets={stop?.real_pallets}
          equivalentPallets={stop?.equivalent_pallets}
          typePallets={stop?.type_pallets}
        />
      </div>
      <div className="cell cell__dark cell__right" {...cellWidth('72px')}>
        <WeightCell weight={stop?.total_weight} />
      </div>
      <div className="cell cell__dark cell__right" {...cellWidth('50px')}>
        <HeightCell height={stop?.item_height} />
      </div>
      <div className="cell cell__grow cell__dark">
        {stop?.liftgate_needed && (
          <div className="cell__extra" data-testId="cells-liftgate">
            <Icon glyph="extras-liftgate" size="2.4rem" color="grey500" />
          </div>
        )}
        {stop?.side_load_needed && (
          <div className="cell__extra" data-testId="cells-sideload">
            <Icon glyph="extras-sideload" size="2.4rem" color="grey500" />
          </div>
        )}
        {stop?.topload_needed && (
          <div className="cell__extra" data-testId="cells-topload">
            <Icon glyph="extras-retractableroof" size="2.4rem" color="grey500" />
          </div>
        )}
      </div>
    </div>
  );
};

export { RoutingSummaryStop };

export default RoutingStop;
